"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALL_FINANCIAL_PERMISSIONS = exports.FinancialPermissions = void 0;
/**
 * Permission string catalog for the Financial bounded context (Stage
 * 5C.5). Format `<module>:<action>`, same convention as
 * `platform.permissions.ts`/`patient.permissions.ts`/
 * `appointment.permissions.ts`. Added now, additively — no permission
 * catalog existed for Financial before this stage, since nothing
 * needed RBAC gating until an HTTP layer existed to gate. This file
 * adds no new business rule; every permission here gates a workflow
 * whose rule already lives inside a frozen Domain Service (Stage
 * 5C.2) or Application Service (Stage 5C.3).
 *
 * One permission per REST resource group, not per individual
 * operation (`patient.permissions.ts`'s own established convention,
 * applied here) — `FINANCIAL_ADVANCE_DEPOSIT_MANAGE` covers credit,
 * debit, AND settle, `FINANCIAL_PAYMENT_PROCESS` covers plain payment
 * creation and every Billing operation that ends in one.
 *
 * This stage's own brief names illustrative examples
 * (`FINANCIAL_VIEW`/`FINANCIAL_CREATE`/`FINANCIAL_UPDATE`/
 * `FINANCIAL_DELETE`) shaped like generic CRUD permissions — but none
 * of the 17 real Financial endpoints (Stage 5C.4's own contract
 * audit) are generic CRUD; every one is a specific named workflow
 * (process a payment, apply a discount, resolve an approval chain).
 * Modeled per-workflow-group instead, matching how this API surface
 * actually shapes up rather than forcing a CRUD taxonomy onto it.
 * `FINANCIAL_PAYMENT_PROCESS`/`FINANCIAL_REFUND_PROCESS`/
 * `FINANCIAL_RECEIPT_MANAGE`/`FINANCIAL_POSTING_EXECUTE`/
 * `FINANCIAL_APPROVAL_READ` map directly onto this stage's own
 * `PAYMENT_PROCESS`/`PAYMENT_REFUND`/`RECEIPT_CREATE`/
 * `POSTING_EXECUTE`/`APPROVAL_EXECUTE` examples, renamed only to
 * follow the real codebase-wide naming convention.
 */
exports.FinancialPermissions = {
    /** createPayment, billAndCreatePayment, processCorporateBilling, processInsuranceBilling — every operation that ends in a real FinPayment. */
    FINANCIAL_PAYMENT_PROCESS: 'financial:payment:process',
    /** issueReceipt, cancelReceipt. */
    FINANCIAL_RECEIPT_MANAGE: 'financial:receipt:manage',
    /** creditAdvanceDeposit, debitAdvanceDeposit, settleDeposit, billAgainstAdvance. */
    FINANCIAL_ADVANCE_DEPOSIT_MANAGE: 'financial:advance-deposit:manage',
    /** processRefund. */
    FINANCIAL_REFUND_PROCESS: 'financial:refund:process',
    /**
     * Decide a PENDING OP refund request — desk, 2026-09-12: an approval gate
     * in front of the immediate refund flow above. Deliberately its OWN
     * permission, not `FINANCIAL_REFUND_PROCESS` reused: the whole point of the
     * gate is that the person who can SUBMIT a refund request must not
     * necessarily be the person who can RELEASE the money, mirroring
     * `FINANCIAL_OP_BILL_DISCOUNT_APPROVE`'s separation from ordinary billing.
     * Catalogue existence alone grants nobody anything — see that permission's
     * own history — it still needs a `RolePermission` grant to whichever role
     * (e.g. Main Cash Counter) is meant to approve.
     */
    FINANCIAL_REFUND_APPROVE: 'financial:refund:approve',
    /** Request a correction to an already-collected payment's mode/bank account/reference number (`FinPaymentLine`) — creates a PENDING row, moves nothing. */
    FINANCIAL_PAYMENT_CORRECTION_REQUEST: 'financial:payment-correction:request',
    /**
     * Decide a PENDING payment correction request — SAME SEPARATION REASONING
     * as `FINANCIAL_REFUND_APPROVE` directly above: the front-desk clerk who
     * can REQUEST a correction must not be the one who can APPROVE it, since
     * approval is what actually posts a reclassifying journal entry and
     * rewrites the payment line. Catalogue existence alone grants nobody
     * anything — deliberately left ungranted to any role but SuperAdmin
     * (the deploy-seed script's default auto-grant) so "must be approved by
     * SuperAdmin" is enforced by this permission simply never being handed
     * to Front Desk/Cashier/Accounts Officer roles.
     */
    FINANCIAL_PAYMENT_CORRECTION_APPROVE: 'financial:payment-correction:approve',
    /** activateTariff. */
    FINANCIAL_TARIFF_MANAGE: 'financial:tariff:manage',
    /** activatePackage. */
    FINANCIAL_PACKAGE_MANAGE: 'financial:package:manage',
    /** applyDiscount. */
    FINANCIAL_DISCOUNT_APPLY: 'financial:discount:apply',
    /** recordPostingEvent. */
    FINANCIAL_POSTING_EXECUTE: 'financial:posting:execute',
    /** resolveApprovalChain — a read, gated separately from the workflows it gates. */
    FINANCIAL_APPROVAL_READ: 'financial:approval:read',
    /** computeChargeBilling — pure computation, no money movement, gated separately from the payment-creating Billing operations above. */
    FINANCIAL_BILLING_COMPUTE: 'financial:billing:compute',
    // Enterprise Architecture Handbook v1.0 (OP Billing Administration,
    // Module 3) — admin CRUD over configuration masters that previously
    // had no HTTP exposure at all (FinCostCenter, FinRevenueCenter,
    // FinPostingRule, FinDepartmentRevenueMapping) or only a single
    // runtime action endpoint (FinTariff's activate, FinDiscountRule's
    // apply — both keep their existing MANAGE/APPLY permission above,
    // unchanged). READ/MANAGE split follows this file's own established
    // pattern (FINANCIAL_APPROVAL_READ).
    /** list/get FinTariff headers (DRAFT authoring) — activation stays on FINANCIAL_TARIFF_MANAGE. */
    FINANCIAL_TARIFF_READ: 'financial:tariff:read',
    /** list/get FinDiscountRule configuration rows. */
    FINANCIAL_DISCOUNT_RULE_READ: 'financial:discount-rule:read',
    /** create/update/activate/deactivate FinDiscountRule configuration rows. */
    FINANCIAL_DISCOUNT_RULE_MANAGE: 'financial:discount-rule:manage',
    /** list/get FinPostingRule rows. */
    FINANCIAL_POSTING_RULE_READ: 'financial:posting-rule:read',
    /** create/update/activate/deactivate FinPostingRule rows. */
    FINANCIAL_POSTING_RULE_MANAGE: 'financial:posting-rule:manage',
    /** list/get FinCostCenter/FinRevenueCenter rows. */
    FINANCIAL_COST_CENTER_READ: 'financial:cost-center:read',
    /** create/update/activate/deactivate FinCostCenter/FinRevenueCenter rows. */
    FINANCIAL_COST_CENTER_MANAGE: 'financial:cost-center:manage',
    /** Cost Center Hub: prepare and submit a monthly allocation run (the run is approved by someone else). */
    FINANCIAL_COST_CENTER_ALLOCATE: 'financial:cost-center:allocate',
    /** Cost Center Hub: approve or reject a submitted allocation run. Never the person who prepared it. */
    FINANCIAL_COST_CENTER_ALLOCATE_APPROVE: 'financial:cost-center:allocate-approve',
    /** Cost Center Hub: request and verify a cost-center correction on a posted ledger line. */
    FINANCIAL_COST_CENTER_RECLASS: 'financial:cost-center:reclass',
    /** Cost Center Hub: approve a correction, which posts the reclass voucher. Never the requester or the verifier. */
    FINANCIAL_COST_CENTER_RECLASS_APPROVE: 'financial:cost-center:reclass-approve',
    /** list/get FinDepartmentRevenueMapping rows. */
    FINANCIAL_DEPARTMENT_MAPPING_READ: 'financial:department-mapping:read',
    /** create/update/delete FinDepartmentRevenueMapping rows. */
    FINANCIAL_DEPARTMENT_MAPPING_MANAGE: 'financial:department-mapping:manage',
    // --- ADR-081 Phase 1 — Finance Masters -------------------------------
    // A separate family from FINANCIAL_COST_CENTER_*: a cost centre is a
    // reporting dimension, a chart-of-accounts row is the ledger money lands in.
    // Granting one must never grant the other (ADR-081 D-5).
    /** list/get FinAccount (chart of accounts) rows. */
    FINANCIAL_ACCOUNT_READ: 'financial:account:read',
    /** create/update/activate/deactivate FinAccount rows. Closes audit finding F-9. */
    FINANCIAL_ACCOUNT_MANAGE: 'financial:account:manage',
    /** list/get FinPostingGroup rows. */
    FINANCIAL_POSTING_GROUP_READ: 'financial:posting-group:read',
    /** create/update/activate/deactivate FinPostingGroup rows. */
    FINANCIAL_POSTING_GROUP_MANAGE: 'financial:posting-group:manage',
    /** list/get FinPostingProfile rows (accounting policy). */
    FINANCIAL_POSTING_PROFILE_READ: 'financial:posting-profile:read',
    /** create/update/activate/deactivate FinPostingProfile rows. */
    FINANCIAL_POSTING_PROFILE_MANAGE: 'financial:posting-profile:manage',
    /** list/get FinRevenueMapping rows (posting group x posting profile cell). */
    FINANCIAL_REVENUE_MAPPING_READ: 'financial:revenue-mapping:read',
    /** create/update/activate/deactivate FinRevenueMapping rows. */
    FINANCIAL_REVENUE_MAPPING_MANAGE: 'financial:revenue-mapping:manage',
    /** list/get TaxRateMaster rows (GST/tax rate configuration). */
    FINANCIAL_TAX_RATE_READ: 'financial:tax-rate:read',
    /** create/update/activate/deactivate TaxRateMaster rows. */
    FINANCIAL_TAX_RATE_MANAGE: 'financial:tax-rate:manage',
    /** list/get PriceOverrideRule rows. */
    FINANCIAL_PRICE_OVERRIDE_READ: 'financial:price-override:read',
    /** create/update/activate/deactivate PriceOverrideRule rows. */
    FINANCIAL_PRICE_OVERRIDE_MANAGE: 'financial:price-override:manage',
    /** read payment gateway configuration (non-secret fields only — mirrors ABHA settings' precedent). */
    FINANCIAL_PAYMENT_GATEWAY_SETTINGS_READ: 'financial:payment-gateway-settings:read',
    /** update payment gateway configuration. */
    FINANCIAL_PAYMENT_GATEWAY_SETTINGS_MANAGE: 'financial:payment-gateway-settings:manage',
    // Enterprise Architecture Handbook v1.0 (IP Billing Administration, Module 4).
    /** list/get FinPackage headers + FinPackageItem rows (DRAFT authoring) — activation stays on FINANCIAL_PACKAGE_MANAGE. */
    FINANCIAL_PACKAGE_READ: 'financial:package:read',
    /** list/get/create/update/activate/deactivate FinDepositRule configuration rows. */
    FINANCIAL_DEPOSIT_RULE_READ: 'financial:deposit-rule:read',
    FINANCIAL_DEPOSIT_RULE_MANAGE: 'financial:deposit-rule:manage',
    /** MLC (Medico-Legal Case) billing rule configuration. */
    FINANCIAL_MLC_RULE_READ: 'financial:mlc-rule:read',
    FINANCIAL_MLC_RULE_MANAGE: 'financial:mlc-rule:manage',
    /** Room/bed upgrade-downgrade billing policy configuration. */
    FINANCIAL_ROOM_TRANSITION_RULE_READ: 'financial:room-transition-rule:read',
    FINANCIAL_ROOM_TRANSITION_RULE_MANAGE: 'financial:room-transition-rule:manage',
    /** Bed reservation hold-duration policy (tenant-wide settings, not a list). */
    FINANCIAL_BED_RESERVATION_SETTINGS_READ: 'financial:bed-reservation-settings:read',
    FINANCIAL_BED_RESERVATION_SETTINGS_MANAGE: 'financial:bed-reservation-settings:manage',
    // -------------------------------------------------------------------------
    // OP Billing Workstation — ADR-062 §C13.
    //
    // Deliberately finer-grained than the "one permission per resource group"
    // convention above, because these gate genuinely different levels of
    // financial authority rather than different operations on one resource: a
    // clerk who may post a bill must NOT thereby be able to cancel one, reprint
    // a duplicate, or override a resolved rate. Each is separately auditable
    // (§C13) and separately testable (Gate 12).
    // -------------------------------------------------------------------------
    /** Create/edit/hold/resume a draft, and post it. The everyday counter action. */
    FINANCIAL_OP_BILL_CREATE: 'financial:op-bill:create',
    /** Reissue an unpaid bill (cancel + recreate atomically). */
    FINANCIAL_OP_BILL_EDIT: 'financial:op-bill:edit',
    /** Cancel an unpaid bill. A PAID bill can never be cancelled (§C8). */
    FINANCIAL_OP_BILL_CANCEL: 'financial:op-bill:cancel',
    /** Reprint marked DUPLICATE. Separate from ordinary printing, which every biller may do. */
    FINANCIAL_OP_BILL_REPRINT: 'financial:op-bill:reprint',
    /** Override a rate resolved by ADR-045's resolver. Highest financial authority. */
    FINANCIAL_OP_BILL_OVERRIDE_RATE: 'financial:op-bill:override-rate',
    /** Approve a discount above policy (§C13). */
    FINANCIAL_OP_BILL_DISCOUNT_APPROVE: 'financial:op-bill:discount-approve',
    /**
     * Post a bill again after the server has said it repeats one raised in the last
     * 30 minutes (`FIN_OP_BILL_REPEAT_SUSPECTED`). Posting at all stays
     * `receivable:invoice:create`; THIS is the separate authority to answer "bill
     * again?" with a reason. Without it the question is refused, never skipped.
     */
    FINANCIAL_OP_BILL_REPEAT_OVERRIDE: 'financial:op-bill:repeat-override',
    /** The admin-only Config tab (§C12.1, §C13). Seeded to Administrator alone. */
    FINANCIAL_OP_BILLING_CONFIG_MANAGE: 'financial:op-billing:config:manage',
    // -------------------------------------------------------------------------
    // OP Billing Register — ADR-084 §7. The official OP revenue register.
    //
    // FIVE, NOT ONE, and each boundary is a real one rather than a nicety:
    //
    //   view / export        Reading the register on screen and extracting the
    //                        whole revenue book to a file are different acts. A
    //                        cashier reconciling their own shift needs the first
    //                        and has no business with the second, and that is the
    //                        distinction an information-security review cares
    //                        about. Export is NOT implied by view.
    //   view-financial       Ledger codes, GL amounts and posting status are
    //                        accounting-department detail, not counter detail.
    //   view-journal         The journal drill-down reaches entries that are not
    //                        OP billing's at all.
    //
    // Enforced SERVER-SIDE: a caller without `view-financial` never receives the
    // ledger fields in the payload. A field hidden only in the UI is a
    // decoration, not a permission.
    // -------------------------------------------------------------------------
    /** The register grid, its filters and its summary cards. */
    FINANCIAL_OP_REGISTER_VIEW: 'financial:op-register:view',
    /**
     * THE EXCEL AND THE PDF — the register exactly as the screen shows it.
     *
     * One row per bill, the Collection tab's own columns, in its order. This is
     * the counter's own reconcile: what they billed today, matched against the
     * grid they are looking at. Deliberately not implied by `view`.
     */
    FINANCIAL_OP_REGISTER_EXPORT: 'financial:op-register:export',
    /**
     * THE DETAILED CSV — one row per service charge line, full end-to-end trail.
     *
     * Service code and name, quantity, unit rate, line discount and %, taxable,
     * GST rate and amount, line net, plus the bill-level tender split, cashier
     * and counter on the anchor row. It is the finance / audit extract, not a
     * shift handover, so it is its OWN grant rather than a property of `export`
     * — the same reasoning that keeps `view-all` separate from `view`.
     */
    FINANCIAL_OP_REGISTER_EXPORT_DETAIL: 'financial:op-register:export-detail',
    /** The A3 landscape print format. */
    FINANCIAL_OP_REGISTER_PRINT: 'financial:op-register:print',
    /** Revenue ledger, posting status and GL amounts on each row. */
    FINANCIAL_OP_REGISTER_VIEW_FINANCIAL: 'financial:op-register:view-financial',
    /** Journal entry numbers and the journal drill-down. */
    FINANCIAL_OP_REGISTER_VIEW_JOURNAL: 'financial:op-register:view-journal',
    /**
     * SEE OTHER PEOPLE'S MONEY.
     *
     * Without it, every register read is narrowed SERVER-SIDE to the calling
     * user's own collections, own counters and own cash sessions — see
     * `resolveReportScope`. With it, the caller may read every login, every
     * counter and every session in the tenant.
     *
     * It is a SEPARATE permission from `view` rather than a property of a role
     * because the two questions are different: "may this person read the
     * register at all" and "may this person read a colleague's takings". A
     * counter cashier reconciling their own shift needs the first and must not
     * have the second — a shift handover is not an audit licence.
     *
     * DENY-BY-DEFAULT IS THE WHOLE POINT. The scope resolver returns SELF unless
     * this permission is present, so a permission that is missing, misspelt or
     * never seeded fails CLOSED (you see only yourself) rather than open. A new
     * report endpoint that forgets to call the resolver is caught by the
     * `register-scope` guard test, which fails if any query builds a `where`
     * without one.
     */
    FINANCIAL_OP_REGISTER_VIEW_ALL: 'financial:op-register:view-all',
    /**
     * SEE THE MAIN COUNTER'S OWN ROW in a counter-wise breakdown (desk,
     * 2026-09-24: "hide main counter in this query to every user just main
     * counter show only admin and main counter users. other user should not
     * see main counter values").
     *
     * SEPARATE FROM `view-all` ON PURPOSE. `view-all` already lets a caller
     * see every OTHER counter's figures; the Main Counter is asked to be MORE
     * restricted than that, not equally so — it is where every other
     * counter's shift settles into, so its total is closer to the day's whole
     * takings than any one counter's own. A caller with `view-all` but not
     * this permission sees every counter EXCEPT Main; the row is simply
     * absent from the breakdown, not zeroed or blanked, the same "absent
     * rather than a lying value" rule `UNAVAILABLE_FIELDS` already follows.
     *
     * Granted automatically to SuperAdmin (deploy-time catalog seeding grants
     * every permission to that system role); granted explicitly to whichever
     * role(s) are the counter's own operators — identified by
     * `Role.defaultCounterId` pointing at the `CashCounter` row whose
     * `counterType` is `MAIN_COUNTER` — so the counter's own cashier still
     * sees their own collections.
     */
    FINANCIAL_OP_REGISTER_VIEW_MAIN_COUNTER: 'financial:op-register:view-main-counter',
};
/** Every Financial permission, flattened — used to seed the `permissions` table. */
exports.ALL_FINANCIAL_PERMISSIONS = Object.values(exports.FinancialPermissions);
//# sourceMappingURL=financial.permissions.js.map